import { useEffect, useState } from 'react';
import { decode } from 'blurhash';

import { buildImgixSrc } from 'shared/components/imgix';

// imgix recommends requesting blurhash at a small width (20-50px) — see
// https://docs.imgix.com/en-US/apis/rendering/format/output-format#blurhash
const DECODE_WIDTH = 32;
const DECODE_HEIGHT = 18;

function decodeToDataUrl(hash: string): string | null {
  const pixels = decode(hash, DECODE_WIDTH, DECODE_HEIGHT);

  const canvas = document.createElement('canvas');
  canvas.width = DECODE_WIDTH;
  canvas.height = DECODE_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }

  const imageData = context.createImageData(DECODE_WIDTH, DECODE_HEIGHT);
  imageData.data.set(pixels);
  context.putImageData(imageData, 0, 0);

  return canvas.toDataURL();
}

// Module-scoped cache so multiple carousel mounts (or re-renders) sharing the same
// dispensary logo don't re-fetch/re-decode the same blurhash repeatedly. `inFlight`
// dedupes concurrent requests for the same URL made before the first one resolves.
const dataUrlCache = new Map<string, string | null>();
const inFlight = new Map<string, Promise<string | null>>();

async function fetchBlurhashDataUrl(imageUrl: string): Promise<string | null> {
  if (dataUrlCache.has(imageUrl)) {
    return dataUrlCache.get(imageUrl) ?? null;
  }

  const existing = inFlight.get(imageUrl);
  if (existing) {
    return existing;
  }

  const promise = (async (): Promise<string | null> => {
    try {
      // buildImgixSrc handles S3-to-Imgix URL conversion and normalizes invalid sources,
      // so this works whether logoImage is already an Imgix URL or a raw upload source.
      const blurhashUrl = buildImgixSrc(imageUrl, { fm: 'blurhash', w: DECODE_WIDTH });
      const response = await fetch(blurhashUrl);
      if (!response.ok) {
        throw new Error(`Unexpected status ${response.status} fetching blurhash`);
      }
      const hash = (await response.text()).trim();
      const dataUrl = decodeToDataUrl(hash);
      dataUrlCache.set(imageUrl, dataUrl);
      return dataUrl;
    } catch {
      dataUrlCache.set(imageUrl, null);
      return null;
    } finally {
      inFlight.delete(imageUrl);
    }
  })();

  inFlight.set(imageUrl, promise);
  return promise;
}

/**
 * Fetches an imgix blurhash string for the given image and decodes it into a tiny bitmap
 * data URL. Upscaled via CSS `object-fit: cover`, this naturally renders as a soft, blurred,
 * distorted version of the source image — the actual logo's shape/colors, just softened,
 * rather than a separately-generated abstract gradient unrelated to the logo's appearance.
 */
export function useBlurhashDataUrl(imageUrl?: string | null): string | null {
  const [dataUrl, setDataUrl] = useState<string | null>(() => (imageUrl ? dataUrlCache.get(imageUrl) ?? null : null));

  useEffect(() => {
    if (!imageUrl) {
      setDataUrl(null);
      return undefined;
    }

    let cancelled = false;

    void fetchBlurhashDataUrl(imageUrl).then((result) => {
      if (!cancelled) {
        setDataUrl(result);
      }
    });

    return (): void => {
      cancelled = true;
    };
  }, [imageUrl]);

  return dataUrl;
}
